import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

// Types
export interface Channel {
  id: string;
  name: string;
  color: string;
  volume: number;
  muted: boolean;
  solo: boolean;
  steps: boolean[]; // 16 steps (true = active)
  frozen?: boolean; // Track is frozen
  frozenAudioUrl?: string; // URL to frozen audio
  effectsCount?: number; // Number of effects (for CPU calculation)
}

export interface Pattern {
  id: string;
  name: string;
  length: number; // Number of steps (16, 32, 64)
  channels: string[]; // Channel IDs
}

export type ViewType = 'pattern' | 'playlist' | 'mixer' | 'pianoroll';

// MIDI Learn Types
export interface MIDIMapping {
  id: string;
  cc: number; // MIDI CC number (0-127)
  channel: number; // MIDI channel (1-16)
  parameter: string; // Parameter ID (e.g., 'masterVolume', 'channel-1-volume')
  parameterLabel: string; // Human-readable label
  min: number; // Minimum value
  max: number; // Maximum value
  curve: 'linear' | 'exponential' | 'logarithmic';
}

// Recording Types
export interface RecordingTake {
  id: string;
  trackId: string;
  audioUrl: string;
  startTime: number; // in beats
  duration: number; // in seconds
  waveform: number[]; // Cached waveform data
  selected: boolean; // Selected for comping
  timestamp: number; // Unix timestamp when recorded
}

export interface CompRegion {
  takeId: string;
  startTime: number; // in beats
  endTime: number; // in beats
}

// Audio Slicer Types
export interface AudioSlice {
  id: string;
  startTime: number; // in seconds
  endTime: number; // in seconds
  midiNote: number; // MIDI note to trigger slice (C1-C8, 36-96)
  audioUrl: string; // URL to sliced audio segment
}

export interface SliceMarker {
  time: number; // in seconds
  id: string;
}

// Marker Types
export interface Marker {
  id: string;
  time: number; // in beats
  name: string;
  color: string;
}

// Tempo Track Types
export interface TempoPoint {
  time: number; // in beats
  bpm: number; // Beats per minute
}

// Routing Matrix Types
export interface RouteConnection {
  trackId: string;
  busId: string;
  enabled: boolean;
  gain: number; // 0-1
}

export interface Bus {
  id: string;
  name: string;
  color: string;
}

// Store Interface
export interface StudioStore {
  // Transport state
  isPlaying: boolean;
  bpm: number;
  currentStep: number;
  
  // View state
  activeView: ViewType;
  
  // Channel state
  channels: Channel[];
  selectedChannelId: string | null;
  
  // Pattern state
  patterns: Pattern[];
  activePatternId: string;
  
  // Advanced Features Panel state
  showAdvancedFeaturesPanel: boolean;
  
  // MIDI Learn state
  midiMappings: MIDIMapping[];
  
  // Recording state
  recordingTakes: RecordingTake[];
  compRegions: CompRegion[];
  isRecording: boolean;
  recordingStartTime: number | null;
  selectedTrackId: string | null;
  
  // Audio Slicer state
  sliceMarkers: SliceMarker[];
  audioSlices: AudioSlice[];
  slicerAudioUrl: string | null;
  slicerAudioDuration: number;
  slicerSensitivity: number; // 0-1
  
  // Marker state
  markers: Marker[];
  
  // Tempo Track state
  tempoPoints: TempoPoint[];
  
  // Routing Matrix state
  routeConnections: RouteConnection[];
  buses: Bus[];
  
  // Transport actions
  togglePlay: () => void;
  stop: () => void;
  setBpm: (bpm: number) => void;
  setCurrentStep: (step: number) => void;
  
  // View actions
  setActiveView: (view: ViewType) => void;
  
  // Advanced Features Panel actions
  openAdvancedFeaturesPanel: () => void;
  closeAdvancedFeaturesPanel: () => void;
  toggleAdvancedFeaturesPanel: () => void;
  
  // Channel actions
  addChannel: (name: string) => void;
  deleteChannel: (id: string) => void;
  duplicateChannel: (id: string) => void;
  renameChannel: (id: string, name: string) => void;
  selectChannel: (id: string) => void;
  updateChannelVolume: (id: string, volume: number) => void;
  toggleChannelMute: (id: string) => void;
  toggleChannelSolo: (id: string) => void;
  freezeChannel: (id: string) => Promise<void>;
  unfreezeChannel: (id: string) => void;
  isChannelFrozen: (id: string) => boolean;
  
  // Pattern actions
  toggleStep: (channelId: string, step: number) => void;
  
  // MIDI Learn actions
  addMIDIMapping: (mapping: Omit<MIDIMapping, 'id'>) => void;
  removeMIDIMapping: (id: string) => void;
  updateMIDIMapping: (id: string, updates: Partial<MIDIMapping>) => void;
  
  // Recording actions
  addRecordingTake: (take: Omit<RecordingTake, 'id'>) => void;
  removeRecordingTake: (id: string) => void;
  setCompRegions: (regions: CompRegion[]) => void;
  setIsRecording: (isRecording: boolean) => void;
  setRecordingStartTime: (time: number | null) => void;
  setSelectedTrackId: (trackId: string | null) => void;
  
  // Audio Slicer actions
  setSlicerAudioUrl: (url: string | null) => void;
  setSlicerAudioDuration: (duration: number) => void;
  setSlicerSensitivity: (sensitivity: number) => void;
  setSliceMarkers: (markers: SliceMarker[]) => void;
  addSliceMarker: (time: number) => void;
  removeSliceMarker: (id: string) => void;
  updateSliceMarker: (id: string, time: number) => void;
  setAudioSlices: (slices: AudioSlice[]) => void;
  clearSlicerState: () => void;
  
  // Marker actions
  addMarker: (marker: Omit<Marker, 'id'>) => void;
  removeMarker: (id: string) => void;
  updateMarker: (id: string, updates: Partial<Marker>) => void;
  
  // Tempo Track actions
  addTempoPoint: (point: TempoPoint) => void;
  removeTempoPoint: (time: number) => void;
  updateTempoPoint: (time: number, bpm: number) => void;
  getTempoAtTime: (time: number) => number;
  
  // Routing Matrix actions
  setRouteConnections: (connections: RouteConnection[]) => void;
  toggleRouteConnection: (trackId: string, busId: string) => void;
  updateRouteConnection: (trackId: string, busId: string, updates: Partial<RouteConnection>) => void;
  saveRoutingConfiguration: () => void;
  loadRoutingConfiguration: () => void;
  resetRoutingToDefault: () => void;
}

// Helper function to generate random color
const getRandomColor = (): string => {
  const colors = [
    '#ef4444', // red
    '#3b82f6', // blue
    '#10b981', // green
    '#8b5cf6', // purple
    '#f59e0b', // amber
    '#ec4899', // pink
    '#06b6d4', // cyan
    '#f97316', // orange
  ];
  return colors[Math.floor(Math.random() * colors.length)];
};

// Helper to convert AudioBuffer to WAV format
const audioBufferToWav = (buffer: AudioBuffer): ArrayBuffer => {
  const numberOfChannels = buffer.numberOfChannels;
  const length = buffer.length * numberOfChannels * 2;
  const arrayBuffer = new ArrayBuffer(44 + length);
  const view = new DataView(arrayBuffer);
  
  // Write WAV header
  const writeString = (offset: number, string: string) => {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  };
  
  writeString(0, 'RIFF');
  view.setUint32(4, 36 + length, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numberOfChannels, true);
  view.setUint32(24, buffer.sampleRate, true);
  view.setUint32(28, buffer.sampleRate * numberOfChannels * 2, true);
  view.setUint16(32, numberOfChannels * 2, true);
  view.setUint16(34, 16, true);
  writeString(36, 'data');
  view.setUint32(40, length, true);
  
  // Write audio data
  const channels: Float32Array[] = [];
  for (let i = 0; i < numberOfChannels; i++) {
    channels.push(buffer.getChannelData(i));
  }
  
  let offset = 44;
  for (let i = 0; i < buffer.length; i++) {
    for (let channel = 0; channel < numberOfChannels; channel++) {
      const sample = Math.max(-1, Math.min(1, channels[channel][i]));
      view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7FFF, true);
      offset += 2;
    }
  }
  
  return arrayBuffer;
};

// Initial mock channels
const initialChannels: Channel[] = [
  {
    id: '1',
    name: 'Kick',
    color: '#ef4444',
    volume: 0.8,
    muted: false,
    solo: false,
    steps: Array(16).fill(false),
  },
  {
    id: '2',
    name: 'Snare',
    color: '#3b82f6',
    volume: 0.7,
    muted: false,
    solo: false,
    steps: Array(16).fill(false),
  },
  {
    id: '3',
    name: 'Hi-Hat',
    color: '#10b981',
    volume: 0.6,
    muted: false,
    solo: false,
    steps: Array(16).fill(false),
  },
  {
    id: '4',
    name: 'Bass',
    color: '#8b5cf6',
    volume: 0.9,
    muted: false,
    solo: false,
    steps: Array(16).fill(false),
  },
];

// Initial pattern
const initialPattern: Pattern = {
  id: 'pattern-1',
  name: 'Pattern 1',
  length: 16,
  channels: ['1', '2', '3', '4'],
};

// Create store with Redux DevTools integration
export const useStudioStore = create<StudioStore>()(
  devtools(
    (set) => ({
      // Initial transport state
      isPlaying: false,
      bpm: 120,
      currentStep: 0,
      
      // Initial view state
      activeView: 'pattern',
      
      // Initial channel state
      channels: initialChannels,
      selectedChannelId: null,
      
      // Initial pattern state
      patterns: [initialPattern],
      activePatternId: 'pattern-1',
      
      // Initial Advanced Features Panel state
      showAdvancedFeaturesPanel: false,
      
      // Initial MIDI Learn state
      midiMappings: [],
      
      // Initial Recording state
      recordingTakes: [],
      compRegions: [],
      isRecording: false,
      recordingStartTime: null,
      selectedTrackId: null,
      
      // Initial Audio Slicer state
      sliceMarkers: [],
      audioSlices: [],
      slicerAudioUrl: null,
      slicerAudioDuration: 0,
      slicerSensitivity: 0.5,
      
      // Initial Marker state
      markers: [],
      
      // Initial Tempo Track state
      tempoPoints: [],
      
      // Initial Routing Matrix state
      routeConnections: [],
      buses: [
        { id: 'bus-master', name: 'Master', color: '#6366f1' },
        { id: 'bus-a', name: 'Bus A', color: '#8b5cf6' },
        { id: 'bus-b', name: 'Bus B', color: '#ec4899' },
        { id: 'bus-reverb', name: 'Reverb', color: '#3b82f6' },
        { id: 'bus-delay', name: 'Delay', color: '#10b981' },
      ],
      
      // Transport actions
      togglePlay: () =>
        set((state) => ({ isPlaying: !state.isPlaying }), false, 'togglePlay'),
      
      stop: () =>
        set({ isPlaying: false, currentStep: 0 }, false, 'stop'),
      
      setBpm: (bpm: number) =>
        set({ bpm: Math.max(60, Math.min(200, bpm)) }, false, 'setBpm'),
      
      setCurrentStep: (step: number) =>
        set({ currentStep: step }, false, 'setCurrentStep'),
      
      // View actions
      setActiveView: (view: ViewType) =>
        set({ activeView: view }, false, 'setActiveView'),
      
      // Advanced Features Panel actions
      openAdvancedFeaturesPanel: () =>
        set({ showAdvancedFeaturesPanel: true }, false, 'openAdvancedFeaturesPanel'),
      
      closeAdvancedFeaturesPanel: () =>
        set({ showAdvancedFeaturesPanel: false }, false, 'closeAdvancedFeaturesPanel'),
      
      toggleAdvancedFeaturesPanel: () =>
        set(
          (state) => ({ showAdvancedFeaturesPanel: !state.showAdvancedFeaturesPanel }),
          false,
          'toggleAdvancedFeaturesPanel'
        ),
      
      // Channel actions
      addChannel: (name: string) =>
        set(
          (state) => {
            const newChannel: Channel = {
              id: crypto.randomUUID(),
              name,
              color: getRandomColor(),
              volume: 0.8,
              muted: false,
              solo: false,
              steps: Array(16).fill(false),
            };
            return { channels: [...state.channels, newChannel] };
          },
          false,
          'addChannel'
        ),
      
      deleteChannel: (id: string) =>
        set(
          (state) => ({
            channels: state.channels.filter((ch) => ch.id !== id),
            selectedChannelId:
              state.selectedChannelId === id ? null : state.selectedChannelId,
          }),
          false,
          'deleteChannel'
        ),
      
      duplicateChannel: (id: string) =>
        set(
          (state) => {
            const channelToDuplicate = state.channels.find((ch) => ch.id === id);
            if (!channelToDuplicate) return state;
            
            const newChannel: Channel = {
              ...channelToDuplicate,
              id: crypto.randomUUID(),
              name: `${channelToDuplicate.name} (Copy)`,
              steps: [...channelToDuplicate.steps],
            };
            return { channels: [...state.channels, newChannel] };
          },
          false,
          'duplicateChannel'
        ),
      
      renameChannel: (id: string, name: string) =>
        set(
          (state) => ({
            channels: state.channels.map((ch) =>
              ch.id === id ? { ...ch, name } : ch
            ),
          }),
          false,
          'renameChannel'
        ),
      
      selectChannel: (id: string) =>
        set({ selectedChannelId: id }, false, 'selectChannel'),
      
      updateChannelVolume: (id: string, volume: number) =>
        set(
          (state) => ({
            channels: state.channels.map((ch) =>
              ch.id === id ? { ...ch, volume: Math.max(0, Math.min(1, volume)) } : ch
            ),
          }),
          false,
          'updateChannelVolume'
        ),
      
      toggleChannelMute: (id: string) =>
        set(
          (state) => ({
            channels: state.channels.map((ch) =>
              ch.id === id ? { ...ch, muted: !ch.muted } : ch
            ),
          }),
          false,
          'toggleChannelMute'
        ),
      
      toggleChannelSolo: (id: string) =>
        set(
          (state) => ({
            channels: state.channels.map((ch) =>
              ch.id === id ? { ...ch, solo: !ch.solo } : ch
            ),
          }),
          false,
          'toggleChannelSolo'
        ),
      
      freezeChannel: async (id: string) => {
        const state = useStudioStore.getState();
        const channel = state.channels.find((ch) => ch.id === id);
        
        if (!channel || channel.frozen) {
          return;
        }
        
        try {
          // Create audio context for offline rendering
          const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
          const sampleRate = audioContext.sampleRate;
          const duration = 10; // 10 seconds default
          const numberOfChannels = 2;
          
          // Create offline context for rendering
          const offlineContext = new OfflineAudioContext(
            numberOfChannels,
            sampleRate * duration,
            sampleRate
          );
          
          // Render the audio (simplified - in production would include actual audio sources)
          const renderedBuffer = await offlineContext.startRendering();
          
          // Convert AudioBuffer to WAV blob
          const wav = audioBufferToWav(renderedBuffer);
          const blob = new Blob([wav], { type: 'audio/wav' });
          
          // Create object URL for the frozen audio
          const frozenAudioUrl = URL.createObjectURL(blob);
          
          // Update channel state
          set(
            (state) => ({
              channels: state.channels.map((ch) =>
                ch.id === id
                  ? {
                      ...ch,
                      frozen: true,
                      frozenAudioUrl,
                      effectsCount: ch.effectsCount || 0,
                    }
                  : ch
              ),
            }),
            false,
            'freezeChannel'
          );
          
          console.log(`Channel ${id} frozen successfully`);
        } catch (error) {
          console.error('Failed to freeze channel:', error);
          throw error;
        }
      },
      
      unfreezeChannel: (id: string) => {
        const state = useStudioStore.getState();
        const channel = state.channels.find((ch) => ch.id === id);
        
        if (!channel || !channel.frozen) {
          return;
        }
        
        // Revoke the frozen audio URL to free memory
        if (channel.frozenAudioUrl) {
          URL.revokeObjectURL(channel.frozenAudioUrl);
        }
        
        // Restore original state
        set(
          (state) => ({
            channels: state.channels.map((ch) =>
              ch.id === id
                ? {
                    ...ch,
                    frozen: false,
                    frozenAudioUrl: undefined,
                  }
                : ch
            ),
          }),
          false,
          'unfreezeChannel'
        );
        
        console.log(`Channel ${id} unfrozen successfully`);
      },
      
      isChannelFrozen: (id: string) => {
        const state = useStudioStore.getState();
        const channel = state.channels.find((ch) => ch.id === id);
        return channel?.frozen || false;
      },
      
      // Pattern actions
      toggleStep: (channelId: string, step: number) =>
        set(
          (state) => ({
            channels: state.channels.map((ch) =>
              ch.id === channelId
                ? {
                    ...ch,
                    steps: ch.steps.map((active, idx) =>
                      idx === step ? !active : active
                    ),
                  }
                : ch
            ),
          }),
          false,
          'toggleStep'
        ),
      
      // MIDI Learn actions
      addMIDIMapping: (mapping: Omit<MIDIMapping, 'id'>) =>
        set(
          (state) => {
            const newMapping: MIDIMapping = {
              ...mapping,
              id: crypto.randomUUID(),
            };
            return { midiMappings: [...state.midiMappings, newMapping] };
          },
          false,
          'addMIDIMapping'
        ),
      
      removeMIDIMapping: (id: string) =>
        set(
          (state) => ({
            midiMappings: state.midiMappings.filter((m) => m.id !== id),
          }),
          false,
          'removeMIDIMapping'
        ),
      
      updateMIDIMapping: (id: string, updates: Partial<MIDIMapping>) =>
        set(
          (state) => ({
            midiMappings: state.midiMappings.map((m) =>
              m.id === id ? { ...m, ...updates } : m
            ),
          }),
          false,
          'updateMIDIMapping'
        ),
      
      // Recording actions
      addRecordingTake: (take: Omit<RecordingTake, 'id'>) =>
        set(
          (state) => {
            const newTake: RecordingTake = {
              ...take,
              id: crypto.randomUUID(),
            };
            return { recordingTakes: [...state.recordingTakes, newTake] };
          },
          false,
          'addRecordingTake'
        ),
      
      removeRecordingTake: (id: string) =>
        set(
          (state) => ({
            recordingTakes: state.recordingTakes.filter((t) => t.id !== id),
          }),
          false,
          'removeRecordingTake'
        ),
      
      setCompRegions: (regions: CompRegion[]) =>
        set({ compRegions: regions }, false, 'setCompRegions'),
      
      setIsRecording: (isRecording: boolean) =>
        set({ isRecording }, false, 'setIsRecording'),
      
      setRecordingStartTime: (time: number | null) =>
        set({ recordingStartTime: time }, false, 'setRecordingStartTime'),
      
      setSelectedTrackId: (trackId: string | null) =>
        set({ selectedTrackId: trackId }, false, 'setSelectedTrackId'),
      
      // Audio Slicer actions
      setSlicerAudioUrl: (url: string | null) =>
        set({ slicerAudioUrl: url }, false, 'setSlicerAudioUrl'),
      
      setSlicerAudioDuration: (duration: number) =>
        set({ slicerAudioDuration: duration }, false, 'setSlicerAudioDuration'),
      
      setSlicerSensitivity: (sensitivity: number) =>
        set(
          { slicerSensitivity: Math.max(0, Math.min(1, sensitivity)) },
          false,
          'setSlicerSensitivity'
        ),
      
      setSliceMarkers: (markers: SliceMarker[]) =>
        set({ sliceMarkers: markers }, false, 'setSliceMarkers'),
      
      addSliceMarker: (time: number) =>
        set(
          (state) => {
            const newMarker: SliceMarker = {
              id: crypto.randomUUID(),
              time,
            };
            const markers = [...state.sliceMarkers, newMarker].sort((a, b) => a.time - b.time);
            return { sliceMarkers: markers };
          },
          false,
          'addSliceMarker'
        ),
      
      removeSliceMarker: (id: string) =>
        set(
          (state) => ({
            sliceMarkers: state.sliceMarkers.filter((m) => m.id !== id),
          }),
          false,
          'removeSliceMarker'
        ),
      
      updateSliceMarker: (id: string, time: number) =>
        set(
          (state) => {
            const markers = state.sliceMarkers
              .map((m) => (m.id === id ? { ...m, time } : m))
              .sort((a, b) => a.time - b.time);
            return { sliceMarkers: markers };
          },
          false,
          'updateSliceMarker'
        ),
      
      setAudioSlices: (slices: AudioSlice[]) =>
        set({ audioSlices: slices }, false, 'setAudioSlices'),
      
      clearSlicerState: () =>
        set(
          {
            sliceMarkers: [],
            audioSlices: [],
            slicerAudioUrl: null,
            slicerAudioDuration: 0,
            slicerSensitivity: 0.5,
          },
          false,
          'clearSlicerState'
        ),
      
      // Marker actions
      addMarker: (marker: Omit<Marker, 'id'>) =>
        set(
          (state) => {
            const newMarker: Marker = {
              ...marker,
              id: crypto.randomUUID(),
            };
            const markers = [...state.markers, newMarker].sort((a, b) => a.time - b.time);
            return { markers };
          },
          false,
          'addMarker'
        ),
      
      removeMarker: (id: string) =>
        set(
          (state) => ({
            markers: state.markers.filter((m) => m.id !== id),
          }),
          false,
          'removeMarker'
        ),
      
      updateMarker: (id: string, updates: Partial<Marker>) =>
        set(
          (state) => {
            const markers = state.markers
              .map((m) => (m.id === id ? { ...m, ...updates } : m))
              .sort((a, b) => a.time - b.time);
            return { markers };
          },
          false,
          'updateMarker'
        ),
      
      // Tempo Track actions
      addTempoPoint: (point: TempoPoint) =>
        set(
          (state) => {
            // Check if a point already exists at this time
            const existingIndex = state.tempoPoints.findIndex(
              (p) => Math.abs(p.time - point.time) < 0.01
            );
            
            if (existingIndex >= 0) {
              // Update existing point
              const points = [...state.tempoPoints];
              points[existingIndex] = point;
              return { tempoPoints: points.sort((a, b) => a.time - b.time) };
            } else {
              // Add new point
              const points = [...state.tempoPoints, point].sort((a, b) => a.time - b.time);
              return { tempoPoints: points };
            }
          },
          false,
          'addTempoPoint'
        ),
      
      removeTempoPoint: (time: number) =>
        set(
          (state) => ({
            tempoPoints: state.tempoPoints.filter((p) => Math.abs(p.time - time) > 0.01),
          }),
          false,
          'removeTempoPoint'
        ),
      
      updateTempoPoint: (time: number, bpm: number) =>
        set(
          (state) => {
            const points = state.tempoPoints.map((p) =>
              Math.abs(p.time - time) < 0.01 ? { ...p, bpm } : p
            );
            return { tempoPoints: points };
          },
          false,
          'updateTempoPoint'
        ),
      
      getTempoAtTime: (time: number) => {
        const state = useStudioStore.getState();
        
        // If no tempo points, return current BPM
        if (state.tempoPoints.length === 0) {
          return state.bpm;
        }
        
        // Find the two points surrounding the given time
        let beforePoint: TempoPoint | null = null;
        let afterPoint: TempoPoint | null = null;
        
        for (const point of state.tempoPoints) {
          if (point.time <= time) {
            beforePoint = point;
          } else if (point.time > time && !afterPoint) {
            afterPoint = point;
            break;
          }
        }
        
        // If time is before first point, return first point's BPM
        if (!beforePoint && afterPoint) {
          return afterPoint.bpm;
        }
        
        // If time is after last point, return last point's BPM
        if (beforePoint && !afterPoint) {
          return beforePoint.bpm;
        }
        
        // If time is between two points, interpolate
        if (beforePoint && afterPoint) {
          const timeDiff = afterPoint.time - beforePoint.time;
          const bpmDiff = afterPoint.bpm - beforePoint.bpm;
          const progress = (time - beforePoint.time) / timeDiff;
          return beforePoint.bpm + bpmDiff * progress;
        }
        
        // Fallback to current BPM
        return state.bpm;
      },
      
      // Routing Matrix actions
      setRouteConnections: (connections: RouteConnection[]) =>
        set({ routeConnections: connections }, false, 'setRouteConnections'),
      
      toggleRouteConnection: (trackId: string, busId: string) =>
        set(
          (state) => {
            const existing = state.routeConnections.find(
              (c) => c.trackId === trackId && c.busId === busId
            );
            
            if (existing) {
              // Toggle existing connection
              return {
                routeConnections: state.routeConnections.map((c) =>
                  c.trackId === trackId && c.busId === busId
                    ? { ...c, enabled: !c.enabled }
                    : c
                ),
              };
            } else {
              // Create new connection
              return {
                routeConnections: [
                  ...state.routeConnections,
                  {
                    trackId,
                    busId,
                    enabled: true,
                    gain: 1.0,
                  },
                ],
              };
            }
          },
          false,
          'toggleRouteConnection'
        ),
      
      updateRouteConnection: (trackId: string, busId: string, updates: Partial<RouteConnection>) =>
        set(
          (state) => ({
            routeConnections: state.routeConnections.map((c) =>
              c.trackId === trackId && c.busId === busId ? { ...c, ...updates } : c
            ),
          }),
          false,
          'updateRouteConnection'
        ),
      
      saveRoutingConfiguration: () => {
        const state = useStudioStore.getState();
        try {
          localStorage.setItem('routing-matrix-config', JSON.stringify(state.routeConnections));
          console.log('Routing configuration saved');
        } catch (error) {
          console.error('Failed to save routing configuration:', error);
        }
      },
      
      loadRoutingConfiguration: () => {
        try {
          const saved = localStorage.getItem('routing-matrix-config');
          if (saved) {
            const connections = JSON.parse(saved);
            useStudioStore.setState({ routeConnections: connections });
            console.log('Routing configuration loaded');
          }
        } catch (error) {
          console.error('Failed to load routing configuration:', error);
        }
      },
      
      resetRoutingToDefault: () =>
        set(
          (state) => {
            // Create default routing: all channels to master bus
            const defaultConnections: RouteConnection[] = state.channels.map((channel) => ({
              trackId: channel.id,
              busId: 'bus-master',
              enabled: true,
              gain: 1.0,
            }));
            return { routeConnections: defaultConnections };
          },
          false,
          'resetRoutingToDefault'
        ),
    }),
    {
      name: 'StudioStore',
      enabled: true, // Enable Redux DevTools
    }
  )
);
